// Per-collection key registry for per-record conflict detection.
//
// Purpose: the top-level 409 handler in usePersistence.js compares changed
// top-level keys.  For collection keys (arrays / keyed maps) that comparison
// is too coarse — two users editing different records both mutate the same
// top-level key, triggering the SaveConflictBanner unnecessarily.
//
// This module extends the comparison to record granularity: given a changed
// collection key, compute which record IDs changed on each side.  Only a
// same-record collision is a genuine overlap → banner.  Different records →
// merge and silent retry.
//
// Engine-owned field carve-outs (B2):
//   Several server-side sweepers append to, or set specific fields on, records
//   that users also edit — e.g. vendor-reassessment-watch appends to
//   `vendor.reassessmentEscalations` while the user edits `vendor.name`.
//   After B1, this still counts as a same-record overlap → banner.
//
//   ENGINE_OWNED_FIELDS lists, per collection, the fields that are exclusively
//   written by sweepers/engines (never by a direct user save through
//   state-write).  When the ONLY external changes on a same-record overlap are
//   to engine-owned fields, hasRecordLevelOverlap returns 'engine-only' instead
//   of true.  The caller (usePersistence) then merges silently:
//     • user's non-engine edits are applied over the fresh state, AND
//     • the specific engine-owned fields the sweeper changed are restored from
//       the fresh server copy (so escalation entries, invite tokens etc. survive).
//   Only the externally-changed engine fields are restored; engine-owned fields
//   the sweeper did NOT touch keep the user's value — preserving user intent.
//
//   This mirrors the slice-restore pattern in azure-functions/state-write/
//   index.js (lines 114–190) which restores `magicLink`, `customThemes`, and
//   `documents` from a JSON_QUERY slice so client snapshots cannot wipe them.
//
// Conservative fallback contract:
//   Any collection key NOT in COLLECTION_REGISTRY, any serialisation failure,
//   or any unresolvable record ID falls back to "treat the whole key as
//   changed" — the same behaviour as today.  This ensures we can never silently
//   drop an edit.

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

// Entry shape:
//   { type: 'array',  idKey: string }          — Array<{ [idKey]: string, … }>
//   { type: 'map' }                             — { [recordKey]: any }
//   { type: 'nested-array', arrayField: string, idKey: string }
//                                                — { [arrayField]: Array<{ [idKey]: … }>, … }
//     for a config object whose editable collection lives one level down
//     (e.g. state.criticalityScale.levels). Record identity + diffing works
//     the same as 'array', just unwrapped from the container object first;
//     any other top-level field on the container (e.g. updatedAt/updatedBy)
//     is merged the same way mergeMap merges keys.
//
// 'enabledObligations' uses 'obligationId' rather than the conventional 'id'.
// All other known arrays use 'id'.

export const COLLECTION_REGISTRY = {
  // Arrays
  vendors: { type: 'array', idKey: 'id' },

  // assets / biaAssessments (ASBC-04, ADR-0005 §8 open item 2): as of
  // ASBC-04 these two no longer travel in the blob SAVE payload — they are
  // stripped client-side (usePersistence.js ASSET_BLOB_KEYS) and written
  // per-record via assets-write. They stay registered here deliberately,
  // for the same reason controlOwnership/controlEffectiveness/soaData do
  // below: they still live in the in-memory `state` object (state-read
  // overlays them from dbo.tenant_asset_entities on every load/refresh), so
  // they can still appear as "changed top-level keys" in a blob-level 409
  // caused by a completely unrelated key. Without an entry here that would
  // fall through to the conservative "cannot determine → treat as overlap"
  // path and pop the conflict banner for a conflict that was never actually
  // in the blob write. With the entry, a different-asset/BIA change
  // classifies as a safe per-record merge instead — it plays no part in
  // assets-write's own conflict handling.
  assets: { type: 'array', idKey: 'id' },
  biaAssessments: { type: 'array', idKey: 'id' },
  employees: { type: 'array', idKey: 'id' },
  incidents: { type: 'array', idKey: 'id' },
  adhocTasks: { type: 'array', idKey: 'id' },
  artefacts: { type: 'array', idKey: 'id' },
  enabledObligations: { type: 'array', idKey: 'obligationId' },
  documents: { type: 'array', idKey: 'id' },
  deletedDocuments: { type: 'array', idKey: 'id' },
  aiTrainingPairs: { type: 'array', idKey: 'id' },
  findings: { type: 'array', idKey: 'id' },
  // findingProposals (SKIE-03) is a READ-ONLY overlay that state-write strips,
  // so it never travels in a save payload — but it does live in the in-memory
  // state object (state-read repopulates it on every load/refresh), so a
  // sweeper run landing between a read and a save can still make it show up as
  // a 'changed top-level key' in a blob 409 caused by something completely
  // unrelated. Registered for the same reason controlOwnership/soaData are:
  // without an entry it falls through to the conservative 'cannot determine →
  // treat as overlap' path and pops the conflict banner for a conflict that was
  // never in the write.
  findingProposals: { type: 'array', idKey: 'id' },
  evidenceRequests: { type: 'array', idKey: 'id' },
  auditProgrammes: { type: 'array', idKey: 'id' },
  internalAudits: { type: 'array', idKey: 'id' },
  // PEA-01 (GH #7123): external audits with their audit days.
  externalAudits: { type: 'array', idKey: 'id' },
  // BCR-02 (msr 2.5b): restore tests, written only by restore-tests-write.
  restoreTests: { type: 'array', idKey: 'id' },
  // AIM-02 (msr 2.5b): asset reviews and review cycles, written only by
  // asset-reviews-write.
  assetReviews: { type: 'array', idKey: 'id' },
  assetReviewCycles: { type: 'array', idKey: 'id' },
  // BCR-03 / TPS-05 (msr 4.5a): BIA reviews, cycles and supplier
  // declarations (bia-reviews-write) and vendor exit plans (vendors-write
  // set-exit-plan) — server-stamped, append-only.
  biaReviews: { type: 'array', idKey: 'id' },
  biaReviewCycles: { type: 'array', idKey: 'id' },
  biaSupplierDeclarations: { type: 'array', idKey: 'id' },
  vendorExitPlans: { type: 'array', idKey: 'id' },
  // CTX-04 (msr 4.5b): obligation evaluations (obligation-evaluations-write)
  // and first-enable anchors (obligations-enable, state-write) —
  // server-stamped, append-only.
  obligationEvaluations: { type: 'array', idKey: 'id' },
  obligationCycleAnchors: { type: 'array', idKey: 'id' },

  // gapAnalyses (issue #5037, QA25E-gap step 3): gap-analysis sessions are the
  // same missing-registration gap as ropaEntries/dpias and the BCDR keys above.
  // Without an entry, getChangedRecordIds returns null, hasRecordLevelOverlap
  // reports "cannot determine", and the 409 handler takes its conservative path
  // — which for this key means a FALSE conflict (two sessions scoring two
  // different gap analyses, or one session racing an unrelated writer on the
  // shared tenant_state version) is escalated into a REAL revert of the user's
  // whole gap analysis. Records are plain { id, … } objects created with
  // generateId() in GapAnalysis.jsx. Two sessions editing the SAME analysis
  // still classify as a genuine overlap and still raise the banner — this only
  // stops different-record edits from being thrown away.
  gapAnalyses: { type: 'array', idKey: 'id' },

  // ropaEntries / dpias (issue #4855): GDPR privacy records, same missing-
  // registration gap as the other arrays above — two tabs editing two
  // DIFFERENT entries in either collection fell through to the conservative
  // whole-key fallback and falsely conflicted. Both use the conventional
  // 'id' field (src/components/ropa/ROPAFormModal.jsx, src/components/
  // privacy/DPIAWorkflow.jsx). NOTE: a broader audit of every other
  // still-unregistered array-typed state key (docs/state-schema.md lists
  // ~65) is a separate, larger task — this PR registers only the two
  // confirmed by #4855, not a full sweep.
  ropaEntries: { type: 'array', idKey: 'id' },
  dpias: { type: 'array', idKey: 'id' },

  // trainingModules / trainingPrograms (#4904/#4905): another instance of the
  // same missing-registration gap as ropaEntries/dpias above. A conflict
  // touching either key fell through to the conservative whole-key fallback
  // (getChangedRecordIds returns null for an unregistered key), which forces
  // hasRecordLevelOverlap's caller to treat ANY unresolved key as a genuine
  // overlap — see usePersistence.js's 409 handler, which (before #4904/#4905)
  // discarded ALL pending user changes across EVERY key whenever even one
  // key came back unresolved, not just the offending one. Both arrays are
  // keyed by the conventional 'id' field (src/components/employees/
  // TrainingTab.jsx: generateId()).
  trainingModules: { type: 'array', idKey: 'id' },
  trainingPrograms: { type: 'array', idKey: 'id' },

  // bcdrPlans / bcdrRunbooks / bcdrRunbookRuns / bcdrBackups / bcdrDrTests /
  // bcdrPenTests (issue #4845) and incidentExercises (sibling gap found
  // alongside #4846): six BC/DR collections and the incident-exercise
  // register were never registered here, so two DIFFERENT records within
  // the SAME collection (e.g. two different BCDR plans, or two different
  // DR tests) edited in separate tabs fell through to the conservative
  // whole-key fallback and could falsely conflict instead of safely
  // merging. `incidents` itself was already registered below — this only
  // adds the still-missing BCDR keys and its sibling `incidentExercises`.
  //
  // BCA-04 (ADR-0006 §8 open item 3, same precedent as controlOwnership/
  // soaData above): the six bcdr* keys below no longer travel in the blob
  // SAVE payload — stripped client-side (usePersistence.js BCDR_BLOB_KEYS)
  // and written per-record via bcdr-write. They stay registered here
  // deliberately: they still live in the in-memory `state` object
  // (state-read overlays them from dbo.tenant_bcdr_entities on every
  // load/refresh), so a completely unrelated blob 409 could still surface
  // one of them as a "changed top-level key" purely because a third party's
  // concurrent bcdr-write landed in between the read and the save. Without
  // an entry that would fall through to the conservative "cannot determine
  // → treat as overlap" path; with it, a different-BC/DR-record change
  // classifies as a safe per-record merge instead — the entry keeps
  // blob-409 classification precise and plays no part in bcdr-write's own
  // per-row conflict handling. `incidentExercises` is unaffected by this
  // migration and stays a plain live blob key.
  bcdrPlans: { type: 'array', idKey: 'id' },
  bcdrRunbooks: { type: 'array', idKey: 'id' },
  bcdrRunbookRuns: { type: 'array', idKey: 'id' },
  bcdrBackups: { type: 'array', idKey: 'id' },
  bcdrDrTests: { type: 'array', idKey: 'id' },
  bcdrPenTests: { type: 'array', idKey: 'id' },
  incidentExercises: { type: 'array', idKey: 'id' },

  // Keyed object maps — record key IS the identifier
  controlOverrides: { type: 'map' },
  obligationDecisions: { type: 'map' },
  documentNumberSequences: { type: 'map' },

  // controlOwnership / controlEffectiveness / soaData (CSCB-01, ADR-0004 §8
  // open item 2): these three no longer travel in the blob SAVE payload —
  // they are stripped client-side (usePersistence.js CONTROL_BLOB_KEYS) and
  // written per-record via controls-write. They stay registered here
  // deliberately, though, because they still live in the in-memory `state`
  // object (state-read overlays them from dbo.tenant_control_entities on
  // every load/refresh), so they can still appear as "changed top-level
  // keys" in a blob-level 409 caused by a COMPLETELY UNRELATED key (e.g. two
  // users editing different vendors) purely because a third party's
  // concurrent controls-write landed in between. Without an entry here that
  // would fall through to the conservative "cannot determine → treat as
  // overlap" path and pop the conflict banner for a conflict that was never
  // actually in the blob write. With the entry, a different-control change
  // classifies as a safe per-record merge instead — the entry keeps blob-409
  // classification precise; it plays no part in controls-write's own
  // conflict handling (src/utils/controlOwnershipWrite.js owns that).
  controlOwnership: { type: 'map' },
  controlEffectiveness: { type: 'map' },
  soaData: { type: 'map' },

  // billing (issue #5066): state.billing is the tenant's own invoice details
  // — a flat config object of independent scalar fields (companyName, vatId,
  // addressLine1, billingEmail, billingCadence, …), written whole by
  // BillingSettingsTab's commit(). It was absent from this registry entirely,
  // so two admins editing two DIFFERENT billing fields both changed the same
  // top-level `billing` key, fell through to the conservative "cannot
  // determine → treat as overlap" fallback, and the loser's edit was silently
  // replaced by the winner's fresh state (DB-CONC-002, same shape as the
  // criticalityScale gap below).
  //
  // 'map' is the right type: each field name is its own record key, so a
  // concurrent edit to a different field merges, while two edits to the SAME
  // field correctly still raise the conflict banner rather than picking a
  // winner — which matters here, because these values decide what a customer
  // is billed. Note this is the tenant-facing block only; the issuer-side
  // billing_config row is super-admin-only and does not travel in the blob.
  billing: { type: 'map' },

  // criticalityScale (issue #4913): state.criticalityScale is a single
  // admin-config object ({ levels: [...], updatedAt, updatedBy }), not a
  // top-level array/map, so it was previously absent from this registry
  // entirely — two sessions editing DIFFERENT levels' cosmetic fields (name/
  // description/colour) both changed the same top-level `criticalityScale`
  // key, fell through to the conservative "cannot determine → treat as
  // overlap" fallback, and the loser's edit was silently discarded and
  // replaced by the winner's fresh state (DB-CONC-002, ADR-0002 §2.1 — the
  // single-config-object case, which stays in the tenant_state blob with its
  // existing ROWVERSION; it does not need a dedicated per-record table).
  // `value` (1..N) is renumbered by array position on every save — see
  // CriticalitySettingsTab.jsx `commit()` — so it identifies a level only for
  // as long as the level count stays put. If one session adds or removes a
  // level while another renames one, every value at or after the insertion
  // point now refers to a DIFFERENT level on the two sides, and merging on it
  // silently lands the rename on the wrong level. `positionalIdKey` marks that:
  // when either side's level count has changed, record identity is treated as
  // unresolvable and the whole key falls back to the conservative conflict
  // path, so the user resolves it rather than the merge quietly corrupting the
  // scale every module reads (#5065). Same-count edits — the common case, two
  // admins renaming two different levels — still merge per record as before.
  criticalityScale: {
    type: 'nested-array',
    arrayField: 'levels',
    idKey: 'value',
    positionalIdKey: true,
  },
};

// ---------------------------------------------------------------------------
// Volatile field carve-out
// ---------------------------------------------------------------------------
//
// Fields listed here are stamped fresh on EVERY save of a record — including
// when the save is the echo of the user's own write coming back from a
// background refresh. They must be excluded from record-level diffing,
// otherwise a lone user's own save looks like an external same-record
// conflict (documents-write bumps `_docVersion`/`lastUpdated` on every save;
// a background refresh then re-reads that same doc and the top-level 409
// classifier sees "changed" on a record nobody else touched).
//
// Unlike ENGINE_OWNED_FIELDS (sweeper-only fields, carved out of the *fresh*
// side so their values can be restored), volatile fields are excluded from
// the diff entirely on both sides — a genuine content edit to any other
// field on the same record still counts as a real change.
export const VOLATILE_FIELDS = {
  documents: new Set(['_docVersion', 'lastUpdated']),
};

// ---------------------------------------------------------------------------
// Engine-owned field carve-outs
// ---------------------------------------------------------------------------
//
// Fields listed here are ONLY written by server-side sweepers or integration
// syncs — never by a direct user action through the main app UI.
//
// BEFORE ADDING A FIELD: confirm it is never set by a user-initiated save
// through state-write.  Dual-ownership fields (user-writable AND sweeper-
// writable) must NOT be listed here — a genuine concurrent edit on such a
// field should still show the SaveConflictBanner so the user knows their
// change was overridden.
//
// Sweeper reference:
//   vendors  ← vendor-reassessment-watch (azure-functions/vendor-reassessment-watch/)
//   artefacts ← artefact-verification-sweeper (azure-functions/artefact-verification-sweeper/)
//   assets   ← integration-poll-scheduler → intune-sync / entra-sync
//   employees ← entra-security-posture (azure-functions/entra-security-posture/) — MFA/sign-in/role fields only
//
// Note: employee jmlProcesses are intentionally absent.  jmlProcesses is a
// shared field (users tick checklist items; the jml-deadline-watch sweeper
// appends flags to the same array).  Treating the whole field as engine-owned
// would silently discard concurrent checklist ticks — a real user action that
// deserves the banner.  A future phase may add per-sub-record analysis.
export const ENGINE_OWNED_FIELDS = {
  vendors: new Set([
    'reassessmentEscalations', // append-only ledger keyed by stage+date
    'assessmentInvite', // one-time portal token minted by sweeper
    'pendingArchiveProposal', // flag set when archive cadence fires
  ]),

  artefacts: new Set([
    // ONLY lastReachabilityCheckAt qualifies.  verificationStatus, lastVerified
    // and updatedAt were listed here until #4999 and are dual-ownership (or, for
    // lastVerified, purely user-owned) — exactly what this list's own contract
    // above forbids.  Listing them meant a sweeper pass that flipped an artefact
    // to 'unverified' classified as 'engine-only', so the user's explicit
    // "Mark as Verified" was silently reverted from the fresh server copy with
    // no SaveConflictBanner.  That inverts ARTF-B-001 ("only a human marks
    // 'verified'"): a human decision was being discarded in favour of an
    // automated flag, invisibly.  Ownership, measured against the writers:
    //   verificationStatus  user: ArtefactRegister handleSave / "Mark as
    //                       Verified" / create;  sweeper: externalUrlSweeper.js
    //                       :86 + artefact-verification-sweeper:138 → DUAL
    //   lastVerified        user: the same three sites;  sweeper: never writes
    //                       it (only reads it for the staleness test) → USER
    //   updatedAt           user: handleSave + "Mark as Verified";  sweeper:
    //                       externalUrlSweeper.js:87 + sweeper:139 → DUAL
    //   lastReachabilityCheckAt  sweeper only (:133); no UI writes it → ENGINE
    // A reachability-only pass (a live link: the sweeper stamps the probe time
    // and nothing else) still classifies 'engine-only' and merges silently, so
    // the routine case stays banner-free.
    'lastReachabilityCheckAt', // timestamp set by the HTTP-probe pass
  ]),

  assets: new Set([
    // intune-sync / entra-sync writes — users never edit these in the UI
    'lastSyncedAt',
    'externalId',
    'source',
    'deviceName',
    'operatingSystem',
    'osVersion',
    'manufacturer',
    'model',
    'serialNumber',
    'userPrincipalName',
    'complianceState',
    'enrolledDateTime',
    'lastSyncDateTime',
    'wifiMacAddress',
    'ethernetMacAddress',
    'deviceCategoryDisplayName',
  ]),

  employees: new Set([
    // entra-security-posture writes — users never edit these Entra-sourced
    // fields directly in the UI (see azure-functions/entra-security-posture/)
    'mfaEnabled',
    'mfaMethods',
    'lastSignInAt',
    'lastNonInteractiveSignInAt',
    'entraAdminRoles',
  ]),
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Returns the set of top-level field names that differ between two records.
// Used for field-level analysis within a same-record overlap.
// Conservative: serialisation failures return an empty Set (the caller treats
// the record as if no specific fields changed, which falls through to the
// existing true/real-conflict path).
function getChangedRecordFields(before, after) {
  try {
    if (!before || !after || typeof before !== 'object' || typeof after !== 'object') {
      return new Set();
    }
    const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
    const changed = new Set();
    for (const k of allKeys) {
      if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) changed.add(k);
    }
    return changed;
  } catch {
    return new Set();
  }
}

// Looks up a record by its string ID in an array collection.
function findRecordById(collection, id, idKey) {
  if (!Array.isArray(collection)) return undefined;
  return collection.find((r) => r?.[idKey] != null && String(r[idKey]) === id);
}

// Strips volatile fields (if any) before serialising a record for comparison.
function stableStringify(item, volatileFields) {
  if (!volatileFields || !volatileFields.size || !item || typeof item !== 'object') {
    return JSON.stringify(item);
  }
  const stripped = { ...item };
  for (const f of volatileFields) delete stripped[f];
  return JSON.stringify(stripped);
}

// An absent (null/undefined) side of a comparison is NOT "unresolvable" — it
// means the key simply did not exist yet in that snapshot, so every record on
// the other side is new. Returning null for that case forced the caller down
// the conservative whole-key path, which discards the losing writer's edit
// even when the two writers touched completely different records. That is the
// DB-CONC-002 shape: the first time two sessions concurrently touch a
// collection the tenant has never persisted (an older tenant's
// `criticalityScale`, a module's first two records, …), the loser's change
// vanishes with no merge. A genuinely mismatched shape (a string where an
// array belongs) still returns null — that one really is unresolvable.
const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});

function absentAsArray(v) {
  return v == null ? EMPTY_ARRAY : v;
}

function absentAsObject(v) {
  return v == null ? EMPTY_OBJECT : v;
}

// Returns the set of record IDs that differ between two array snapshots.
// Conservative: serialisation errors → returns null (caller falls back).
// volatileFields (optional Set<string>) are ignored when comparing records —
// see VOLATILE_FIELDS above.
function changedArrayIds(rawBefore, rawAfter, idKey, volatileFields) {
  try {
    const before = absentAsArray(rawBefore);
    const after = absentAsArray(rawAfter);
    const beforeMap = new Map();
    const afterMap = new Map();

    if (!Array.isArray(before) || !Array.isArray(after)) return null;

    for (const item of before) {
      const id = item?.[idKey];
      if (id == null) return null; // unresolvable — fall back
      beforeMap.set(String(id), stableStringify(item, volatileFields));
    }
    for (const item of after) {
      const id = item?.[idKey];
      if (id == null) return null;
      afterMap.set(String(id), stableStringify(item, volatileFields));
    }

    const changed = new Set();
    const allIds = new Set([...beforeMap.keys(), ...afterMap.keys()]);
    for (const id of allIds) {
      if (beforeMap.get(id) !== afterMap.get(id)) changed.add(id);
    }
    return changed;
  } catch {
    return null;
  }
}

// Returns the set of map keys that differ between two object snapshots.
// Conservative: serialisation errors → returns null.
function changedMapKeys(rawBefore, rawAfter) {
  try {
    const before = absentAsObject(rawBefore);
    const after = absentAsObject(rawAfter);
    if (
      before == null ||
      after == null ||
      typeof before !== 'object' ||
      typeof after !== 'object' ||
      Array.isArray(before) ||
      Array.isArray(after)
    )
      return null;

    const changed = new Set();
    const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
    for (const k of allKeys) {
      if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) changed.add(k);
    }
    return changed;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Identity inference for collections with no explicit entry (GH #6607)
// ---------------------------------------------------------------------------
//
// ADR-0002 §9 closed the migration programme on option B — the storage grain
// stays mixed — and ratified the consequence this block implements:
//
//   "A registered blob collection is a supported configuration, not a debt.
//    An unregistered blob collection is still a defect."
//
// Registration was a manual act, and 33 blob-written record collections never
// got one: every privacy collection but `ropaEntries` and `dpias`, plus
// `tasks`, `managementReviews`, `auditCyclePlans`, `certifications`,
// `documentTypes`, `vulnerabilities` and the rest. For those keys
// `getChangedRecordIds` returned null, which is the caller's signal that record
// identity is unresolvable — and usePersistence's 409 resolution answers an
// unresolvable key by REVERTING the user's edit to the server's value and
// raising the conflict banner. So two people editing two different DSR requests
// did not merge; one of them was told to re-apply work the server would have
// accepted. That is the client half of the version-conflict contract the server
// already implements: the 409 carries a snapshot precisely so the client can
// merge, and for these keys the client could not read it.
//
// The registry stays the authority — an explicit entry always wins, and every
// non-obvious case (maps, nested arrays, positional identity, engine-owned
// fields, volatile fields) still needs one. What is inferred is only the
// obvious case: an array whose records already carry a stable surrogate id, on
// every side of the comparison. When identity cannot be established beyond
// doubt the function returns null and the caller takes exactly the same
// conservative path it takes today, so this can widen what merges safely but
// can never narrow what is protected.
//
// The guard is deliberately strict, because the failure mode of inferring
// WRONGLY is the one thing worse than a false conflict: two records judged
// different when they are the same merge silently, and a concurrent edit is
// lost with no banner. Hence: every element on every side must be a plain
// object; it must carry a non-null primitive under the SAME candidate key; and
// that value must be unique within its own side. An array of primitives, a
// heterogeneous array, or an array with duplicate or missing ids infers
// nothing.
//
// Candidates are immutable surrogate keys only. A human-meaningful field
// (`name`, `code`, `key`) can be edited — and an id the user can rename is not
// an identity, it is a value that happens to look like one. The name is only
// half the guard; see isUsableId below for the other half, which is the one
// that does not rest on a naming convention.
const INFERRED_ID_KEYS = ['id', 'uuid', 'recordId'];

/**
 * True for a value usable as an INFERRED record identifier.
 *
 * Strings only, and that restriction is the guard rather than a detail. What
 * makes a per-record merge safe is that the key is a *stable surrogate* — and
 * presence, type and uniqueness do not establish stability. Being spelled `id`
 * does not establish it either; that is a naming convention, and a guard whose
 * safety argument reduces to "a field with this name means X" is documentation,
 * not enforcement.
 *
 * So it refuses the shape in which stability is most often false. A finite
 * NUMBER under a key like this is overwhelmingly an ordinal, and an ordinal
 * stops identifying anything the moment the record count changes: this
 * registry's own `criticalityScale` entry is the standing counterexample — a
 * key renumbered by array position on every save, which landed a rename on the
 * wrong level until #5065 forced the discovery, and which is excluded here only
 * by a `positionalIdKey` flag an inferred spec can never carry.
 *
 * Every collection this inference exists for carries a `generateId()` string,
 * so the restriction is invisible to all 33 of them — it costs nothing today
 * and turns "no collection in the tree does this" from a search result into an
 * invariant that holds for the collection nobody has written yet.
 */
function isUsableId(value) {
  return typeof value === 'string' && value.length > 0;
}

/**
 * True when every element of `arr` is a plain object carrying a usable, unique
 * value at `idKey`. An empty array passes — it constrains nothing, and a
 * collection that is empty on one side is the ordinary "first record added"
 * case rather than a reason to give up on the other two sides.
 */
function sideIdentifiesBy(arr, idKey) {
  const seen = new Set();
  for (const item of arr) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
    const id = item[idKey];
    if (!isUsableId(id)) return false;
    const asString = String(id);
    if (seen.has(asString)) return false; // duplicate — identity is not unique
    seen.add(asString);
  }
  return true;
}

/**
 * Infer an array spec for a key with no explicit registry entry, from the
 * values actually being compared.
 *
 * @param {Array<unknown>} sides  the values on each side of the comparison
 *        (before / after / user). Absent sides are treated as empty arrays,
 *        matching absentAsArray() elsewhere in this file; a side that is
 *        present but not an array defeats inference outright.
 * @returns {{ type: 'array', idKey: string, inferred: true } | null}
 */
export function inferArraySpec(sides) {
  const arrays = [];
  for (const side of sides) {
    const arr = absentAsArray(side);
    if (!Array.isArray(arr)) return null;
    arrays.push(arr);
  }
  // Nothing to identify: every side is empty. There is no record-level
  // distinction to draw, so leave it to the conservative whole-key path.
  if (arrays.every((a) => a.length === 0)) return null;

  for (const idKey of INFERRED_ID_KEYS) {
    if (arrays.every((arr) => sideIdentifiesBy(arr, idKey))) {
      return { type: 'array', idKey, inferred: true };
    }
  }
  return null;
}

/**
 * The spec to use for `key`: the explicit registry entry when there is one,
 * otherwise an identity inferred from the values being compared.
 *
 * Every spec consumer in this file goes through here, so an explicit entry and
 * an inferred one behave identically downstream — including the volatile-field
 * and engine-owned-field lookups, which are keyed on the state key rather than
 * on the spec and so apply to both.
 */
function specFor(key, ...sides) {
  const explicit = COLLECTION_REGISTRY[key];
  if (explicit) return explicit;
  return inferArraySpec(sides);
}

/**
 * getChangedRecordIds(key, before, after)
 *
 * Returns a Set<string> of record identifiers that changed between `before`
 * and `after` for the given top-level state key.
 *
 * Returns null when:
 *   • the key is not in COLLECTION_REGISTRY (unknown collection),
 *   • the values cannot be compared at record granularity (shape mismatch,
 *     missing idKey, serialisation failure).
 *
 * A null return means the caller should treat the whole key as changed
 * (conservative fallback — same as today's top-level behaviour).
 */
export function getChangedRecordIds(key, before, after) {
  return changedRecordIdsForSpec(specFor(key, before, after), key, before, after);
}

/**
 * getChangedRecordIds' body, against an ALREADY-RESOLVED spec.
 *
 * hasRecordLevelOverlap compares three sides pairwise and must use one identity
 * for all of them: resolving the spec separately per pair could infer `id` for
 * one comparison and `uuid` (or nothing) for the other, and two id-sets built
 * on different keys cannot be intersected meaningfully.
 */
function changedRecordIdsForSpec(spec, key, before, after) {
  if (!spec) return null;

  if (spec.type === 'array') {
    return changedArrayIds(before, after, spec.idKey, VOLATILE_FIELDS[key]);
  }
  if (spec.type === 'map') {
    return changedMapKeys(before, after);
  }
  if (spec.type === 'nested-array') {
    const beforeArr = before && typeof before === 'object' ? before[spec.arrayField] : undefined;
    const afterArr = after && typeof after === 'object' ? after[spec.arrayField] : undefined;
    return changedArrayIds(beforeArr, afterArr, spec.idKey, VOLATILE_FIELDS[key]);
  }
  return null; // unknown spec type — conservative fallback
}

/**
 * Reads the array a spec points at, or null when the shape doesn't match.
 */
function collectionArrayFor(spec, value) {
  if (spec.type === 'array') return Array.isArray(value) ? value : null;
  if (spec.type === 'nested-array') {
    const arr = value && typeof value === 'object' ? value[spec.arrayField] : undefined;
    return Array.isArray(arr) ? arr : null;
  }
  return null;
}

/**
 * True when the collection identifies records by a positional key and the
 * record count changed on either side, so the same id no longer refers to the
 * same record across the two versions being compared.
 *
 * Only collections explicitly flagged `positionalIdKey` are affected; every
 * other collection uses a genuinely stable id and is untouched by this.
 */
function positionalIdentityUnreliable(key, externalBefore, externalAfter, userAfter) {
  // Only an EXPLICIT entry can carry positionalIdKey — an inferred spec is
  // only ever inferred from a stable surrogate id, which is the opposite
  // property, so there is nothing to look up for an unregistered key.
  const spec = COLLECTION_REGISTRY[key];
  if (!spec?.positionalIdKey) return false;

  const before = collectionArrayFor(spec, externalBefore);
  const external = collectionArrayFor(spec, externalAfter);
  const user = collectionArrayFor(spec, userAfter);
  // Shape we can't read — the normal path already falls back conservatively.
  if (before === null || external === null || user === null) return false;

  return external.length !== before.length || user.length !== before.length;
}

/**
 * hasRecordLevelOverlap(key, externalBefore, externalAfter, userAfter)
 *
 * Returns a quad-state:
 *   true           — same record(s) changed on both sides, and at least one
 *                    externally-changed field is NOT engine-owned → genuine
 *                    conflict → show SaveConflictBanner.
 *   'engine-only'  — same record(s) changed on both sides, but ALL externally-
 *                    changed fields are in ENGINE_OWNED_FIELDS[key] → sweeper
 *                    race → silent merge with engine-field preservation.
 *   false          — only different records changed → safe to merge.
 *   null           — cannot determine at record granularity → fall back to
 *                    top-level (conservative: treat as overlap).
 *
 * 'externalBefore' is the server state before the concurrent writer committed.
 * 'externalAfter'  is the fresh state after the concurrent writer committed.
 * 'userAfter'      is the state the user was trying to save.
 */
export function hasRecordLevelOverlap(key, externalBefore, externalAfter, userAfter) {
  // A positional idKey stops identifying anything the moment either side
  // changes the record count — see positionalIdKey in the registry above.
  // Merging on it then attributes an edit to the wrong record, which is worse
  // than the conflict banner it was trying to avoid, so fall back to the
  // conservative whole-key path instead (#5065).
  if (positionalIdentityUnreliable(key, externalBefore, externalAfter, userAfter)) return null;

  // One identity for all three sides — see changedRecordIdsForSpec.
  const spec = specFor(key, externalBefore, externalAfter, userAfter);
  const externalChanged = changedRecordIdsForSpec(spec, key, externalBefore, externalAfter);
  const userChanged = changedRecordIdsForSpec(spec, key, externalBefore, userAfter);

  if (externalChanged === null || userChanged === null) return null;

  const engineOwned = ENGINE_OWNED_FIELDS[key];

  let hasEngineOnlyOverlap = false;

  for (const id of externalChanged) {
    if (!userChanged.has(id)) continue; // different records — no overlap here

    // Both sides changed this record to the SAME value — a convergent change,
    // not a collision. The common case is the tab's own side-effect write
    // (obligations-enable, documents-assign …) that it applied locally but
    // did not adopt the version of, because another session wrote in between
    // (GH #7714): treating it as a conflict reverted the whole key and showed a
    // banner on an action that collided with nothing (co-owner review).
    {
      const ext =
        spec?.type === 'array'
          ? findRecordById(externalAfter, id, spec.idKey)
          : externalAfter?.[id];
      const usr =
        spec?.type === 'array' ? findRecordById(userAfter, id, spec.idKey) : userAfter?.[id];
      if (ext !== undefined && JSON.stringify(ext) === JSON.stringify(usr)) continue;
    }

    // Same record changed on both sides.  If this collection has engine-owned
    // field definitions and is an array (field-level analysis requires id lookup),
    // check whether ALL external changes are to engine-owned fields.
    if (engineOwned && spec?.type === 'array') {
      const beforeRecord = findRecordById(externalBefore, id, spec.idKey);
      const afterRecord = findRecordById(externalAfter, id, spec.idKey);
      const externalFieldChanges = getChangedRecordFields(beforeRecord, afterRecord);

      if (
        externalFieldChanges.size > 0 &&
        [...externalFieldChanges].every((f) => engineOwned.has(f))
      ) {
        // All external changes are to engine-owned fields — sweeper race, not a
        // human conflict.  Continue scanning other record IDs; a later ID might
        // still produce a real conflict.
        hasEngineOnlyOverlap = true;
        continue;
      }
    }

    // Either the collection has no engine carve-out, or at least one externally-
    // changed field is non-engine-owned → genuine same-record collision.
    return true;
  }

  if (hasEngineOnlyOverlap) return 'engine-only';
  return false;
}

/**
 * mergeCollectionKey(key, fresh, userValue, preSaveServerValue)
 *
 * Performs a per-record merge for a collection key where no same-record
 * collision exists.  The user's changed records are applied on top of
 * the fresh server state.
 *
 * For arrays: inserts/updates the user's changed records into the fresh
 *   array, and preserves deletions (records present in preSave but absent
 *   in userValue are removed from the fresh array too).
 * For maps: user's changed keys overwrite the fresh map's values.
 *
 * Returns the merged value, or null if the merge cannot be performed
 * safely (caller should fall back to using the whole userValue as-is,
 * which is what the top-level merge does today).
 */
export function mergeCollectionKey(key, fresh, userValue, preSaveServerValue) {
  const spec = specFor(key, fresh, userValue, preSaveServerValue);
  if (!spec) return null;

  try {
    if (spec.type === 'array') {
      return mergeArray(fresh, userValue, preSaveServerValue, spec.idKey);
    }
    if (spec.type === 'map') {
      return mergeMap(fresh, userValue, preSaveServerValue);
    }
    if (spec.type === 'nested-array') {
      return mergeNestedArray(fresh, userValue, preSaveServerValue, spec);
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * mergeWithEngineCarveOut(key, fresh, userValue, preSaveServerValue)
 *
 * Variant of mergeCollectionKey for use when hasRecordLevelOverlap returns
 * 'engine-only'.  Performs the standard per-record merge but additionally
 * restores, for each overlapping record, the specific engine-owned fields that
 * the external writer (sweeper) actually changed — taken from the fresh server
 * copy — so the sweeper's escalation entries, invite tokens, sync timestamps
 * etc. survive the merge.
 *
 * Only the engine-owned fields that the external writer changed are restored.
 * Engine-owned fields the sweeper left unchanged keep whatever value the merge
 * produced (usually the user's value), preserving user intent.  Example: if the
 * sweeper only updated lastReachabilityCheckAt, the user's concurrent change to
 * verificationStatus is kept even though verificationStatus is engine-owned.
 *
 * Falls back to mergeCollectionKey for collections not in ENGINE_OWNED_FIELDS
 * or for non-array collections where field-level analysis is unavailable.
 */
export function mergeWithEngineCarveOut(key, fresh, userValue, preSaveServerValue) {
  const spec = specFor(key, fresh, userValue, preSaveServerValue);
  const engineOwned = ENGINE_OWNED_FIELDS[key];

  // Only array collections support per-record field-level analysis.
  if (!engineOwned || !spec || spec.type !== 'array') {
    return mergeCollectionKey(key, fresh, userValue, preSaveServerValue);
  }

  // Standard per-record merge: user's changed records applied over fresh state.
  const base = mergeCollectionKey(key, fresh, userValue, preSaveServerValue);
  if (!Array.isArray(base)) return base;

  // Build fast ID → record lookups for fresh (server) and preSave snapshots.
  const freshById = new Map(
    Array.isArray(fresh) ? fresh.map((r) => [String(r?.[spec.idKey]), r]) : []
  );
  const preSaveById = new Map(
    Array.isArray(preSaveServerValue)
      ? preSaveServerValue.map((r) => [String(r?.[spec.idKey]), r])
      : []
  );

  return base.map((record) => {
    const id = String(record?.[spec.idKey]);
    const freshRecord = freshById.get(id);
    const preSaveRecord = preSaveById.get(id);

    // New record (user-added) or pre-save lookup impossible — nothing to restore.
    if (!freshRecord || !preSaveRecord) return record;

    // Find which fields the external writer (sweeper) actually changed on this record.
    const externalFieldChanges = getChangedRecordFields(preSaveRecord, freshRecord);
    const engineFieldsChanged = [...externalFieldChanges].filter((f) => engineOwned.has(f));

    if (engineFieldsChanged.length === 0) return record;

    // Restore only those engine-owned fields from the fresh server copy.
    const restored = { ...record };
    for (const field of engineFieldsChanged) {
      if (Object.prototype.hasOwnProperty.call(freshRecord, field)) {
        restored[field] = freshRecord[field];
      } else {
        // Sweeper removed the field — remove from merged record too.
        delete restored[field];
      }
    }
    return restored;
  });
}

function mergeArray(rawFresh, rawUserValue, rawPreSave, idKey) {
  // Same absent-side reasoning as changedArrayIds above: a key that did not
  // exist in the pre-save snapshot (or that the other writer has not created
  // yet) merges as an empty collection. Returning null here made the caller
  // fall back to taking the user's whole value, which silently clobbers the
  // concurrent writer's records instead of merging them.
  const fresh = absentAsArray(rawFresh);
  const userValue = absentAsArray(rawUserValue);
  const preSave = absentAsArray(rawPreSave);
  if (!Array.isArray(fresh) || !Array.isArray(userValue) || !Array.isArray(preSave)) return null;

  // Build maps for fast lookup
  const freshMap = new Map(fresh.map((item) => [String(item?.[idKey]), item]));
  const preSaveMap = new Map(preSave.map((item) => [String(item?.[idKey]), item]));
  const userMap = new Map(userValue.map((item) => [String(item?.[idKey]), item]));

  // Validate all id keys are resolvable
  for (const [k] of [...freshMap, ...preSaveMap, ...userMap]) {
    if (k === 'undefined' || k === 'null') return null;
  }

  // Determine which record IDs the user changed (added/edited/deleted)
  const allIds = new Set([...preSaveMap.keys(), ...userMap.keys()]);
  const userChangedIds = new Set();
  for (const id of allIds) {
    const preSaveJson = JSON.stringify(preSaveMap.get(id));
    const userJson = JSON.stringify(userMap.get(id));
    if (preSaveJson !== userJson) userChangedIds.add(id);
  }

  // Start from fresh; apply user's changed records (additions, edits, deletions)
  const resultMap = new Map(freshMap);
  for (const id of userChangedIds) {
    if (userMap.has(id)) {
      resultMap.set(id, userMap.get(id));
    } else {
      // User deleted this record
      resultMap.delete(id);
    }
  }

  // Preserve original ordering: use the fresh array order as the base,
  // then append any records the user added that aren't in fresh.
  const result = [];
  const seen = new Set();

  for (const item of fresh) {
    const id = String(item?.[idKey]);
    if (resultMap.has(id)) {
      result.push(resultMap.get(id));
      seen.add(id);
    }
    // If the user deleted this record it won't be in resultMap — skip it.
  }

  // Append user-added records (in user-array order)
  for (const item of userValue) {
    const id = String(item?.[idKey]);
    if (!seen.has(id) && resultMap.has(id)) {
      result.push(resultMap.get(id));
      seen.add(id);
    }
  }

  return result;
}

// Merges a 'nested-array' collection: merges the array at spec.arrayField
// the same way mergeArray does, then carries over any other top-level field
// the user changed (e.g. updatedAt/updatedBy stamps) the same way mergeMap
// merges keys — fresh as the base, user's own changed fields applied on top.
function mergeNestedArray(rawFresh, rawUserValue, rawPreSave, spec) {
  // A container object that does not exist yet on one side merges as an empty
  // container (same absent-side reasoning as mergeArray). `criticalityScale`
  // is the live example: a tenant provisioned before the shared scale existed
  // has no `state.criticalityScale` at all, so the first two concurrent edits
  // to it both created the key — and the loser's whole edit was discarded.
  const fresh = absentAsObject(rawFresh);
  const userValue = absentAsObject(rawUserValue);
  const preSave = absentAsObject(rawPreSave);
  if (
    typeof fresh !== 'object' ||
    Array.isArray(fresh) ||
    typeof userValue !== 'object' ||
    Array.isArray(userValue) ||
    typeof preSave !== 'object' ||
    Array.isArray(preSave)
  )
    return null;

  const mergedArr = mergeArray(
    fresh[spec.arrayField],
    userValue[spec.arrayField],
    preSave[spec.arrayField],
    spec.idKey
  );
  if (mergedArr === null) return null;

  const result = { ...fresh, [spec.arrayField]: mergedArr };
  for (const k of Object.keys(userValue)) {
    if (k === spec.arrayField) continue;
    if (JSON.stringify(preSave[k]) !== JSON.stringify(userValue[k])) {
      result[k] = userValue[k];
    }
  }
  return result;
}

function mergeMap(rawFresh, rawUserValue, rawPreSave) {
  // Absent side → empty map (see mergeArray).
  const fresh = absentAsObject(rawFresh);
  const userValue = absentAsObject(rawUserValue);
  const preSave = absentAsObject(rawPreSave);
  if (
    typeof fresh !== 'object' ||
    Array.isArray(fresh) ||
    typeof userValue !== 'object' ||
    Array.isArray(userValue) ||
    typeof preSave !== 'object' ||
    Array.isArray(preSave)
  )
    return null;

  // Find which keys the user changed
  const allKeys = new Set([...Object.keys(preSave), ...Object.keys(userValue)]);
  const userChangedKeys = new Set();
  for (const k of allKeys) {
    if (JSON.stringify(preSave[k]) !== JSON.stringify(userValue[k])) {
      userChangedKeys.add(k);
    }
  }

  // Start from fresh, apply user's changes
  const result = { ...fresh };
  for (const k of userChangedKeys) {
    if (Object.prototype.hasOwnProperty.call(userValue, k)) {
      result[k] = userValue[k];
    } else {
      // User deleted this key
      delete result[k];
    }
  }
  return result;
}
